import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { BalanceBox } from '../../../models/box';
import Title from '../../../components/Title';
import { NavLink, useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useSelection } from '../../../components/context/SelectionContext';
import { useTheme, Tooltip, useMediaQuery, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent } from '@mui/material';
import { Office } from '../../../models/office';
import { Add as AddIcon, Info as InfoIcon, AddAlert as AddAlertIcon, Edit as EditIcon, Delete as DeleteIcon, RemoveRedEye as RemoveRedEyeIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const ListBox: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Administrar Cajas";
    const [box, setBox] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [offices, setOffices] = useState([]);
    const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
    const navigate = useNavigate();
    const { drawerOpen } = useDrawer();

    // Cargar oficinas
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const officesResponse = await axios.get('offices');
                setOffices(officesResponse.data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                //console.log('Error fetching data:', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchData();
    }, []); // Solo se ejecuta una vez al montar el componente

    // Cargar cajas cuando se selecciona una oficina
    useEffect(() => {
        if (office_id) {
            const fetchBoxData = async () => {
                showLoading();
                try {
                    const { data } = await axios.get(`listbox/${office_id}`);
                    setBox(data.data);
                    showAlert('Se ha realizado la consulta exitosamente!', 'info');
                } catch (error) {
                    showAlert('Error fetching data!', 'error');
                } finally {
                    hideLoading();
                }
            };

            fetchBoxData();
        }
    }, [office_id]); // Solo depende de office_id

    const decimalFormatter = new Intl.NumberFormat('en-US', { style: 'decimal' });

    const simpleColumns: SimpleColumn[] = [
        {
            field: 'date',
            headerName: 'Fecha',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }: { row: SimpleRow }) => format(parseISO(String(row.date)), 'yyyy-MM-dd HH:mm:ss'),
        },
        { field: 'office', headerName: 'Oficina', flex: 1, minWidth: 150, wrapText: true },
        { field: 'collect', headerName: 'Cobro', flex: 1, minWidth: 150, wrapText: true },
        {
            field: 'balance',
            headerName: 'Saldo',
            flex: 1,
            minWidth: 150,
            align: 'right',
            renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.balance ?? 0)),
        },
        {
            field: 'opt_detail',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => {
                const numericCollectId = typeof row.collectid === 'number' ? row.collectid : Number(row.collectid);
                const handleClick = () => {
                    // Si es caja principal, collectid viene vacío => dejamos collect_id vacío.
                    if (Number.isFinite(numericCollectId) && numericCollectId > 0) {
                        setCollect_id(String(numericCollectId));
                    } else {
                        setCollect_id('');
                    }
                    navigate(`/administrar/cajas/detail`);
                };
                return (
                    <Tooltip title="Detalle">
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
                            onClick={handleClick}
                        >
                            <ZoomInIcon fontSize="small" />
                        </Button>
                    </Tooltip>
                );
            },
        },
    ];

    const rows: SimpleRow[] = box.map((item: BalanceBox) => ({
        id: item.id as RowId,
        date: item.date,
        office: item.office?.name ?? '',
        collect: item.collect?.name ?? 'Caja Principal',
        balance: item.balance,
        collectid: item.collect?.id ?? '',
    }));

    return (
        <HeaderResponsive >
            <StickyListToolbar>
                <Title>Detalle Caja Principal</Title>
                <Box sx={{ ml: 'auto' }}>
                    <Button
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/cajas/add"
                    >
                        {isMobile ? <AddIcon /> : "Agregar"}
                    </Button>
                </Box>
            </StickyListToolbar>
            <Card
                sx={{
                    width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                }}
            >
                <form onSubmit={(e) => e.preventDefault()}>
                    <CardContent>
                        <Grid item xs={6} sm={6} xl={6} md={6} lg={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="form-layouts-separator-select-label">
                                    Seleccionar Oficina
                                </InputLabel>
                                {offices && offices.length > 0 ? (
                                    <Select
                                        label="Seleccionar Oficina"
                                        value={office_id}
                                        id="form-layouts-separator-select"
                                        labelId="form-layouts-separator-select-label"
                                        onChange={(event) => setOffice_id(event.target.value)}
                                        required
                                    >
                                        {offices.map((office: Office) => (
                                            <MenuItem key={office.id} value={office.id}>
                                                {office.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : null}
                            </FormControl>
                        </Grid>
                    </CardContent>
                </form>
                {box.length > 0 ? (
                    <SimpleCustomTable
                        rows={rows}
                        columns={simpleColumns}
                        maxHeight={600}
                        rowHeight={24}
                        showBorders
                        showCount
                        exportCsv
                        exportFileName="cajas.csv"
                        wrapCellContent
                        columnGroups={[{ header: 'Opciones', fields: ['opt_detail'] }]}
                    />
                ) : (
                    <Box p={3} textAlign="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <InfoIcon color="info" style={{ marginRight: 8 }} />
                            <Typography variant="h6">No existen registros</Typography>
                        </Box>
                    </Box>
                )}
            </Card>
        </HeaderResponsive>
    );
}

export default ListBox;
